import { getAceToggleButtonNames } from "../ace-toggle-button/ace-toggle-button.get-names";
import { h } from "@stencil/core";
import { isPositiveInteger, mergeClassNames } from "../../utils";
export class AceToggleButton {
    constructor() {
        this.type = 'default';
        this.disabled = false;
        this.fullWidth = false;
        this.fullWidthMobile = false;
        this.fullWidthTablet = false;
        this.active = false;
        this.toggleOnClick = true;
        this.size = 'regular';
        this._active = false;
        this._disabled = this.disabled;
    }
    componentWillLoad() {
        var _a;
        this.names = getAceToggleButtonNames((_a = this.hostElement) === null || _a === void 0 ? void 0 : _a.id);
        this.onToggleActiveChanged();
        this.onIconNameChanged();
        this.onDisabledChanged();
    }
    onToggleActiveChanged() {
        this._active = this.active;
    }
    onIconNameChanged() {
        this._iconName = this.iconName;
    }
    onDisabledChanged() {
        this._disabled = this.disabled;
    }
    isCircular() {
        // Check wether the button is icon only or has a number inside.
        if (this._iconName && !this.hostElement.innerHTML) {
            return true;
        }
        return isPositiveInteger(this.hostElement.innerHTML);
    }
    /**
     * @private
     * @return string
     */
    getFinalCssClasses() {
        return mergeClassNames({
            [this.names.CLASS.ROOT]: true,
            [this.names.CLASS.____DEFAULT]: this.type === 'default',
            [this.names.CLASS.____HIDDEN]: this.type === 'hidden',
            [this.names.CLASS.____DEFAULT____ACTIVE]: this.type === 'default' && this._active,
            [this.names.CLASS.____HIDDEN____ACTIVE]: this.type === 'hidden' && this._active,
            [this.names.CLASS.____CIRCULAR]: this.isCircular(),
            [this.names.CLASS.____FULL_WIDTH]: this.fullWidth,
            [this.names.CLASS.____FULL_WIDTH_MOBILE]: this.fullWidthMobile,
            [this.names.CLASS.____FULL_WIDTH_TABLET]: this.fullWidthTablet,
            [this.names.CLASS.____SIZE_SMALL]: this.size === 'small',
            [this.names.CLASS.____SIZE_REGULAR]: this.size === 'regular',
        });
    }
    getIconColor() {
        return this.disabled ? 'text-neutral--disabled' : this._active ? 'text-neutral--interactive-uni-light' : 'text-neutral--interactive';
    }
    handleClick(event) {
        if (this.disabled) {
            event.preventDefault();
            return;
        }
        if (this.toggleOnClick) {
            this.active = !this.active;
        }
        this.buttonClicked.emit();
    }
    render() {
        const isSmall = this.size === 'small';
        // We can't nicely defined this size at CSS level based on the modifier class.
        // TODO: Find a way to map the CSS var.
        const iconSize = isSmall ? 'var(--ace-semantic-buttons-toggle-button-dimension-icon)' : '1.25rem';
        // We can't have this in CSS because the border radius depends on the size and the theme applied (semantic var)
        const customStyles = {
            '--ace-toggle-button-border-radius': isSmall ? 'var(--ace-semantic-buttons-toggle-button-radius)' : 'var(--ace-primitive-radius-full)',
        };
        return (h("button", { key: 'cdea74ca10344c03473d88b03c38c2b0b25d9fd2', name: this.name, style: customStyles, class: this.getFinalCssClasses(), id: this.names.ID.ROOT, "aria-disabled": this._disabled.toString(), onClick: e => this.handleClick(e), "aria-label": !this.hostElement.innerText ? this.name : false }, this._iconName ? h("ace-icon", { name: this._iconName, size: iconSize, color: this.getIconColor() }) : null, h("slot", { key: '2a62f7283a24ce8ab293c59e6b64d9bceb7cd52c' })));
    }
    static get is() { return "ace-toggle-button"; }
    static get encapsulation() { return "shadow"; }
    static get delegatesFocus() { return true; }
    static get originalStyleUrls() {
        return {
            "$": ["./ace-toggle-button.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["ace-toggle-button.css"]
        };
    }
    static get properties() {
        return {
            "type": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "IAceToggleButtonType",
                    "resolved": "\"default\" | \"hidden\"",
                    "references": {
                        "IAceToggleButtonType": {
                            "location": "import",
                            "path": "./ace-toggle-button.types",
                            "id": "src/components/ace-toggle-button/ace-toggle-button.types.ts::IAceToggleButtonType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "type",
                "reflect": false,
                "defaultValue": "'default'"
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "disabled",
                "reflect": true,
                "defaultValue": "false"
            },
            "fullWidth": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "full-width",
                "reflect": false,
                "defaultValue": "false"
            },
            "fullWidthMobile": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "full-width-mobile",
                "reflect": false,
                "defaultValue": "false"
            },
            "fullWidthTablet": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "full-width-tablet",
                "reflect": false,
                "defaultValue": "false"
            },
            "name": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "name",
                "reflect": false
            },
            "active": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "active",
                "reflect": true,
                "defaultValue": "false"
            },
            "iconName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "IAceIconName",
                    "resolved": "string",
                    "references": {
                        "IAceIconName": {
                            "location": "import",
                            "path": "../ace-icon/ace-icon.types",
                            "id": "src/components/ace-icon/ace-icon.types.ts::IAceIconName"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "icon-name",
                "reflect": false
            },
            "toggleOnClick": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "toggle-on-click",
                "reflect": false,
                "defaultValue": "true"
            },
            "size": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "IAceToggleButtonSize",
                    "resolved": "\"regular\" | \"small\"",
                    "references": {
                        "IAceToggleButtonSize": {
                            "location": "import",
                            "path": "./ace-toggle-button.types",
                            "id": "src/components/ace-toggle-button/ace-toggle-button.types.ts::IAceToggleButtonSize"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "size",
                "reflect": false,
                "defaultValue": "'regular'"
            }
        };
    }
    static get events() {
        return [{
                "method": "buttonClicked",
                "name": "buttonClicked",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "MouseEvent",
                    "resolved": "MouseEvent",
                    "references": {
                        "MouseEvent": {
                            "location": "global",
                            "id": "global::MouseEvent"
                        }
                    }
                }
            }];
    }
    static get elementRef() { return "hostElement"; }
    static get watchers() {
        return [{
                "propName": "active",
                "methodName": "onToggleActiveChanged"
            }, {
                "propName": "iconName",
                "methodName": "onIconNameChanged"
            }, {
                "propName": "disabled",
                "methodName": "onDisabledChanged"
            }];
    }
}
//# sourceMappingURL=ace-toggle-button.js.map
